import { formatCurrencyFromCents } from '@/utils/currencyHelpers'

import type { StateBreakdownData } from './stateBreakdownData'

/**
 * A $0 state estimate has several possible causes, and naming the wrong one is
 * worse than saying nothing. In likelihood order: the state's standard
 * deduction covers the income apportioned to it, the state doesn't tax this
 * income at all, or credits already cover what's due. Returns null when the
 * data doesn't identify a cause, so the page stays silent rather than guessing.
 */
export const explainZeroStateEstimate = ({
  data,
  quarter,
}: {
  data: StateBreakdownData
  quarter: string | undefined
}): string | null => {
  if (data.isNoIncomeTaxState) {
    return `${data.state} doesn't tax this kind of income, so there's never an estimate to pay there.`
  }

  const deductionCoversIncome =
    data.taxableIncomeInCents === 0 &&
    data.incomeInCents !== null &&
    data.incomeInCents > 0 &&
    data.stateStandardDeductionInCents !== null &&
    data.stateStandardDeductionInCents > 0

  if (deductionCoversIncome) {
    return `${data.state} leaves the first ${formatCurrencyFromCents(
      data.stateStandardDeductionInCents ?? 0
    )} of income untaxed, and you earned ${formatCurrencyFromCents(
      data.incomeInCents ?? 0
    )} there — less than that — so ${data.state} has nothing to tax this year.`
  }

  if (data.annualLiabilityInCents === 0) {
    return `We don't project any ${data.state} tax for the year, so we don't expect you to owe anything this quarter.`
  }

  const creditsInCents =
    (data.creditedPaymentsInCents ?? 0) +
    (data.withholding?.annualTotalInCents ?? 0)

  if (creditsInCents > 0 && quarter) {
    return `Your ${data.state} payments and withholding already cover the ${
      Number(quarter) * 25
    }% of your projected ${data.state} tax due by Q${quarter}, so we don't expect a payment from you this quarter.`
  }

  return null
}
